import React, { useState, useEffect, useCallback } from 'react';
import WelcomePage from './pages/WelcomePage';
import CafyIntroPage from './pages/CafyIntroPage';
import UpdateGoalsPage from './pages/UpdateGoalsPage';
import GoalsPage from './pages/GoalsPage';
import TrackingPage from './pages/TrackingPage';
import JourneyPage from './pages/JourneyPage';
import ResourcesPage from './pages/RessourcesPage';
import GoalSettingFlow from './components/features/GoalSettingFlow';
import Navigation from './components/layout/Navigation';
import AppLayout from './components/layout/AppLayout';
import { useTaxonomyContext } from './context/TaxonomyContext';
import { useMockUser } from './context/MockUserContext';
import { saveGoalSettings, fetchGoalSettings, deleteGoalSettings, updateGoalPriorityRanks } from './services/goalsApi';
import {
	sortByPriority,
	resequenceRanks,
	changedRanks,
} from './utils/goalPriority';
import './App.css';

const App = () => {
	const { currentUserId } = useMockUser();
	const { getTermById, loading, error } = useTaxonomyContext();
	const [currentPage, setCurrentPage] = useState('welcome');
	const [showWelcome, setShowWelcome] = useState(true);
	const [showGoalSettingFlow, setShowGoalSettingFlow] = useState(false);
	const [goalSettingsLoaded, setGoalSettingsLoaded] = useState(false);
	const [userGoals, setUserGoals] = useState([]);
	const [cameFromWelcome, setCameFromWelcome] = useState(false);
	// ADD: State to control welcome page initial view
	const [welcomeInitialView, setWelcomeInitialView] = useState('welcome');

	// ADD: New state for tracking functionality
	const [currentTrackedGoal, setCurrentTrackedGoal] = useState(null);
	const [currentGoalIndex, setCurrentGoalIndex] = useState(0);

	// Function to check if goals are set - MORE FLEXIBLE CHECK
	const hasGoals = () => {
		const result = userGoals.length > 0; // Simplified check - just need goals
		return result;
	};

	const getGoalDescription = (goalId) =>
		getTermById(goalId)?.goal_setting_message || 'No description available';

	const getSmartWatchValue = (goalId) =>
		getTermById(goalId)?.smart_watch ?? false;

	const transformGoalSettingsResponse = useCallback(
		(data) =>
			data
				.map((item) => {
					const termId = `term_${item.term_id}`;
					const term = getTermById(termId);
					return {
						id: termId,
						goal_setting_id: item.id,
						name: item.term.name,
						smart_watch: term?.smart_watch ?? false,
						goal_description: term?.goal_setting_message || '',
						short_description: term?.short_description || '',
						priority_rank: item.priority_rank ?? 0,
					};
				})
				.sort(sortByPriority),
		[getTermById]
	);

	useEffect(() => {
		if (!currentUserId || loading) return;

		let isCancelled = false;
		setGoalSettingsLoaded(false);

		fetchGoalSettings(currentUserId)
			.then((data) => {
				if (isCancelled) return;

				const goals = transformGoalSettingsResponse(data);
				setUserGoals(goals);
				if (goals.length > 0) {
					setShowWelcome(false);
					setCurrentPage('goals');
					setCameFromWelcome(false);
					setWelcomeInitialView('welcome');
				}
				setCurrentTrackedGoal((previousGoal) => {
					if (!previousGoal) return null;
					return goals.find((goal) => goal.id === previousGoal.id) || null;
				});
				setCurrentGoalIndex((previousIndex) =>
					goals.length > 0 ? Math.min(previousIndex, goals.length - 1) : 0
				);
			})
			.catch((err) => console.error('Failed to load goal settings:', err))
			.finally(() => {
				if (!isCancelled) {
					setGoalSettingsLoaded(true);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [currentUserId, loading, transformGoalSettingsResponse]);

	// Then in handleGoalSettingComplete, change the transformation:
	const handleGoalSettingComplete = (completed = false, selections = []) => {

		setShowGoalSettingFlow(false);

		if (completed && selections && selections.length > 0) {
			// Transform selections to match GoalsPage expected format
			const goalsWithMetadata = selections.map((goal) => ({
				...goal,
				smart_watch: getSmartWatchValue(goal.id),
				goal_description: getGoalDescription(goal.id),
				short_description: goal.short_description,
			}));


			// IMPORTANT: Set goals first, then update other state
			setUserGoals(goalsWithMetadata);

			saveGoalSettings(goalsWithMetadata, currentUserId)
				.then(() => fetchGoalSettings(currentUserId))
				.then((data) => setUserGoals(transformGoalSettingsResponse(data)))
				.catch((err) => console.error('Failed to sync goal settings:', err));

			// Navigate to goals page
			setShowWelcome(false);
			setCurrentPage('goals');
			setCameFromWelcome(false);
			setWelcomeInitialView('welcome'); // Reset welcome view

		} else {
			// If canceled, go back to cafy-intro
			setCurrentPage('cafy-intro');
		}
	};

	// Handle logo click - Navigate back to welcome page
	const handleLogoClick = () => {
		setShowWelcome(true);
		setCurrentPage('welcome');
		setShowGoalSettingFlow(false);
		setCameFromWelcome(false);
		setWelcomeInitialView('welcome'); // Reset to default welcome view
	};

	// Handle getting started from welcome page
	const handleGetStarted = () => {
		setShowWelcome(false);
		setCurrentPage('cafy-intro');
		setCameFromWelcome(false);
	};

	// Handle CAFY button click from navigation - Navigate to welcome page
	const handleCafyClick = () => {
		// Go to the full welcome page with all feature cards
		setShowWelcome(true);
		setCurrentPage('welcome');
		setShowGoalSettingFlow(false);
		setCameFromWelcome(false);
		setWelcomeInitialView('welcome'); // Show the full welcome page
	};

	// UPDATED: Handle navigation - redirect to CAFY intro if no goals
	const handleNavigate = (pageId) => {

		// If no goals are set and trying to access goal-dependent pages, redirect to CAFY intro
		if (
			!hasGoals() &&
			['goals', 'tracking', 'journey', 'resources'].includes(pageId)
		) {
			setShowWelcome(false);
			setCurrentPage('cafy-intro');
			setShowGoalSettingFlow(false);
			setCameFromWelcome(true);
			setWelcomeInitialView('welcome');
			return;
		}

		// Special handling for tracking page - set first goal as current if none selected
		if (pageId === 'tracking' && userGoals.length > 0) {
			// If no current tracked goal, set the first goal as default
			if (!currentTrackedGoal) {
				setCurrentTrackedGoal(userGoals[0]);
				setCurrentGoalIndex(0);
			}
		}

		setCurrentPage(pageId);
		setShowGoalSettingFlow(false);
		setCameFromWelcome(false);
		setWelcomeInitialView('welcome'); // Reset welcome view when navigating
	};

	// UPDATED: Handle feature clicks from circular process (Goals button on welcome page)
	const handleFeatureClick = (feature) => {
		if (feature.id === 'goals-completed') {
			// Handle goal completion from welcome page
			handleGoalSettingComplete(true, feature.selections);
			return;
		}

		// For all goal-dependent features, redirect to CAFY intro if no goals
		if (['goals', 'tracking', 'journey', 'resources'].includes(feature.id)) {
			if (hasGoals()) {
				// If goals are set, go directly to the requested page
				setShowWelcome(false);
				setCurrentPage(feature.id);
			} else {
				// If no goals, go to CAFY intro
				setShowWelcome(false);
				setCurrentPage('cafy-intro');
				setCameFromWelcome(true);
			}
		} else {
			// Other features go directly to their pages
			setShowWelcome(false);
			setCurrentPage(feature.id);
			setCameFromWelcome(false);
		}
		setShowGoalSettingFlow(false);
		setWelcomeInitialView('welcome'); // Reset welcome view
	};

	// Handle going back to welcome from CAFY intro
	const handleCafyCancel = () => {
		if (cameFromWelcome) {
			setShowWelcome(true);
			setCurrentPage('welcome');
			setCameFromWelcome(false);
			setWelcomeInitialView('welcome'); // Reset to default welcome view
		} else {
			// If we have goals, go to goals page, otherwise stay on cafy-intro
			if (hasGoals()) {
				setCurrentPage('goals');
			} else {
				// Stay on cafy-intro or go back to welcome
				setShowWelcome(true);
				setCurrentPage('welcome');
			}
		}
	};

	// Handle navigation to UpdateGoalsPage
	const handleUpdateGoalsViaList = () => {
		setShowWelcome(false);
		setCurrentPage('update-goals');
		setShowGoalSettingFlow(false);
		setCameFromWelcome(false);
		setWelcomeInitialView('welcome');
	};

	// Handle updating goals from UpdateGoalsPage
	const handleUpdateGoalsFromList = (updatedGoals) => {
		const updatedIds = new Set(updatedGoals.map((g) => g.id));
		const toRemove = userGoals.filter((g) => !updatedIds.has(g.id));

		// Same hole to close as on a single delete — this list can drop a ranked
		// goal too — plus any goal just added takes a free slot at the bottom.
		const resequenced = resequenceRanks(updatedGoals);
		const ranks = changedRanks(userGoals, resequenced);

		setUserGoals(resequenced);
		setCurrentPage('goals');

		const idsToRemove = toRemove
			.map((g) => g.goal_setting_id)
			.filter(Boolean);

		const requests = [
			saveGoalSettings(resequenced, currentUserId),
			...(idsToRemove.length > 0
				? [deleteGoalSettings(idsToRemove, currentUserId)]
				: []),
		];

		Promise.all(requests)
			// The ranks are only meaningful once the adds and removes have landed.
			.then(() =>
				ranks.length > 0 ? updateGoalPriorityRanks(ranks, currentUserId) : null
			)
			.then(() => fetchGoalSettings(currentUserId))
			.then((data) => setUserGoals(transformGoalSettingsResponse(data)))
			.catch((err) => console.error('Failed to sync goal settings:', err));
	};

	// Handle saving priority ranks from GoalsPage (reorder mode)
	const handleSavePriorityRanks = (ranks) => {
		// Optimistically apply the new ranks + ordering
		const rankMap = new Map(
			ranks.map((r) => [r.goal_setting_id, r.priority_rank])
		);
		setUserGoals((prev) =>
			[...prev]
				.map((goal) => ({
					...goal,
					priority_rank:
						rankMap.get(goal.goal_setting_id) ?? goal.priority_rank ?? 0,
				}))
				.sort(sortByPriority)
		);

		updateGoalPriorityRanks(ranks, currentUserId)
			.then(() => fetchGoalSettings(currentUserId))
			.then((data) => setUserGoals(transformGoalSettingsResponse(data)))
			.catch((err) =>
				console.error('Failed to save priority ranks:', err)
			);
	};

	// Handle cancel from UpdateGoalsPage
	const handleUpdateGoalsCancel = () => {
		if (userGoals.length > 0) {
			setCurrentPage('goals');
		} else {
			setCurrentPage('cafy-intro');
		}
	};

	// Handle starting goal setting flow from CAFY intro page
	const handleStartGoalSetting = () => {
		setShowGoalSettingFlow(true);
	};

	// Goal management handlers
	const handleDeleteGoal = (goalId) => {
		const goalSettingId = userGoals.find((goal) => goal.id === goalId)?.goal_setting_id;
		const remaining = userGoals.filter((goal) => goal.id !== goalId);
		// Close the hole the removed goal leaves in the 1..N run, and promote
		// whatever now falls inside the cutoff.
		const updatedGoals = resequenceRanks(remaining);
		const ranks = changedRanks(remaining, updatedGoals);
		setUserGoals(updatedGoals);

		// The goal being tracked may be the one just removed, and the index may
		// now point past the end or at the wrong goal. Only the fetch on mount
		// reconciled this, so until a reload the Tracking page went on offering
		// a goal that no longer exists.
		const stillTracked =
			currentTrackedGoal &&
			updatedGoals.some((goal) => goal.id === currentTrackedGoal.id);
		if (stillTracked) {
			setCurrentGoalIndex(
				updatedGoals.findIndex((goal) => goal.id === currentTrackedGoal.id)
			);
		} else {
			setCurrentTrackedGoal(updatedGoals[0] ?? null);
			setCurrentGoalIndex(0);
		}

		if (goalSettingId) {
			// The re-rank has to follow the delete: sent together, it would be
			// applied while the removed goal is still holding its old position.
			deleteGoalSettings([goalSettingId], currentUserId)
				.then(() =>
					ranks.length > 0
						? updateGoalPriorityRanks(ranks, currentUserId)
						: null
				)
				.catch((err) =>
					console.error('Failed to delete goal setting:', err)
				);
		}

		// If no goals left, reset goal completion status and redirect to CAFY intro
		if (updatedGoals.length === 0) {
			// Redirect to CAFY intro page
			setShowWelcome(false);
			setCurrentPage('cafy-intro');
			setShowGoalSettingFlow(false);
			setCameFromWelcome(false); // User didn't come from welcome, came from goals deletion
			setWelcomeInitialView('welcome');
		}

	};

	// UPDATED: Handle tracking goal with goal selection
	const handleTrackGoal = (goalId) => {
		// Find the goal being tracked
		const goalToTrack = userGoals.find((goal) => goal.id === goalId);
		setCurrentTrackedGoal(goalToTrack);
		setCurrentGoalIndex(userGoals.findIndex((goal) => goal.id === goalId));
		setCurrentPage('tracking');
	};

	const handleViewCareTips = (goalId) => {
		setCurrentPage('resources');
	};

	const handleWatchGoal = (goalId) => {
		// TODO: Implement smartwatch integration
	};

	if (loading || !currentUserId || !goalSettingsLoaded) {
		return <div style={{ padding: '2rem' }}>Loading...</div>;
	}
	if (error) return <div style={{ padding: '2rem' }}>Failed to load content. Please refresh.</div>;

	// Show welcome page
	if (showWelcome) {
		return (
			<WelcomePage
				onGetStarted={handleGetStarted}
				onFeatureClick={handleFeatureClick}
				onLogoClick={handleLogoClick}
				onUpdateGoalsViaList={handleUpdateGoalsViaList} // ADD: Pass the handler
				initialView={welcomeInitialView} // Pass the initial view state
			/>
		);
	}

	// Show Goal Setting Flow (full screen, no navigation)
	if (showGoalSettingFlow) {
		return (
			<GoalSettingFlow
				onComplete={handleGoalSettingComplete}
				onCancel={() => handleGoalSettingComplete(false)}
			/>
		);
	}

	// Show UpdateGoalsPage (full screen, no navigation)
	if (currentPage === 'update-goals') {
		return (
			<UpdateGoalsPage
				currentGoals={userGoals}
				onUpdateGoals={handleUpdateGoalsFromList}
				onCancel={handleUpdateGoalsCancel}
			/>
		);
	}

	// Show CafyIntroPage (full screen, no navigation)
	if (currentPage === 'cafy-intro') {
		return (
			<CafyIntroPage
				onStartGoals={handleStartGoalSetting}
				onCancel={cameFromWelcome ? handleCafyCancel : null}
				onUpdateGoalsViaList={handleUpdateGoalsViaList}
			/>
		);
	}

	// Render page content based on current page (with navigation)
	const renderPage = () => {
		switch (currentPage) {
			case 'tracking':
				return (
					<TrackingPage
						onLogoClick={handleLogoClick}
						currentGoal={currentTrackedGoal}
						allGoals={userGoals}
						currentGoalIndex={currentGoalIndex}
						onNextGoal={(nextIndex) => {
							setCurrentGoalIndex(nextIndex);
							setCurrentTrackedGoal(userGoals[nextIndex]);
						}}
						onNavigateToJourney={() => handleNavigate('journey')}
					/>
				);
			case 'journey':
				return <JourneyPage onLogoClick={handleLogoClick} goals={userGoals} />;
			case 'resources':
				return (
					<ResourcesPage goals={userGoals} onLogoClick={handleLogoClick} />
				);
			// Goals is also the fallback for an unrecognised page.
			default:
				return (
					<GoalsPage
						goals={userGoals}
						onDeleteGoal={handleDeleteGoal}
						onTrackGoal={handleTrackGoal}
						onViewCareTips={handleViewCareTips}
						onWatchGoal={handleWatchGoal}
						onUpdateGoals={handleUpdateGoalsViaList}
						onLogoClick={handleLogoClick}
						onSavePriority={handleSavePriorityRanks}
					/>
				);
		}
	};

	// IMPORTANT: Check what we're returning here
	const showNavigation = hasGoals();

	return (
		<AppLayout>
			{/* Show Navigation when goals are set */}
			{showNavigation && (
				<Navigation
					currentPage={currentPage}
					onNavigate={handleNavigate}
					onCafyClick={handleCafyClick}
				/>
			)}
			<main className='app-main'>{renderPage()}</main>
		</AppLayout>
	);
};

export default App;
